import React, { useState } from "react";
import { NavIcons } from "../ui/Icons";
import { formatDateShort } from "../../utils/formatters";
import {
  useDismissedConversations,
  type DismissedConversation,
} from "../../hooks/useDismissedConversations";

// =============================================================================
// DismissedLeadsPanel
// =============================================================================
// Collapsible panel pinned at the very bottom of the Setter Performance tab.
// Surfaces the dismissed/killed conversations that every other DM view hides.
//
// - Collapsed by default. Header shows the COUNT dismissed in the selected range
//   (the "marker"). Click to expand → list → click a row opens the DM modal.
// - Self-contained: owns its own data via useDismissedConversations. The parent
//   only passes scope (org/setters/range/tz) + the modal-open callback so it can
//   reuse the ConversationViewModal the tab already hosts.
// - Shows both `dismissed` (parked, reactivates on new lead msg) and `dead`
//   (killed, permanent) with a distinguishing badge.
// =============================================================================

interface DismissedLeadsPanelProps {
  organizationId: string | null;
  setterIds: string[];
  dateRange: { start: Date | null; end: Date | null };
  timezone: string;
  /** ghl_user_id → setter name, from the tab's already-loaded metrics. */
  setterNames?: Record<string, string>;
  onOpenConversation: (conv: {
    id: string;
    contactName: string;
    leadInstagram: string | null;
  }) => void;
  /** True org-wide total from the server-side leaderboard RPC — NOT the capped list count. */
  total: number;
  byDismisser: { id: string; name: string; count: number }[];
  byAssignee: { ghlUserId: string | null; count: number }[];
  leaderboardError: string | null;
  leaderboardLoading: boolean;
  /** Opens the 3-col grid modal (owned by the tab). */
  onViewGrid: () => void;
}

interface LeaderboardBarRow {
  key: string;
  label: string;
  count: number;
}

/** Horizontal bar cut for the leaderboard strip — top 10 + "+N more" overflow line. */
const LeaderboardBars: React.FC<{ rows: LeaderboardBarRow[] }> = ({ rows }) => {
  const top = rows.slice(0, 10);
  const overflow = rows.length - top.length;
  const maxCount = Math.max(...top.map((r) => r.count), 1);

  return (
    <div className="space-y-2">
      {top.map((r, i) => (
        <div key={r.key}>
          <div className="flex items-baseline gap-1 mb-1">
            <span
              data-testid={i === 0 ? "dismissed-leaderboard-top-name" : undefined}
              className="token-13 text-glass truncate"
            >
              {r.label}
            </span>
            <span className="token-13 text-glass-muted">·</span>
            <span className="token-13m text-glass tabular-nums">{r.count}</span>
          </div>
          <div className="h-1.5 bg-black/[0.06] rounded-full overflow-hidden">
            <div
              className="h-full bg-ds-accent/50 rounded-full"
              style={{ width: `${(r.count / maxCount) * 100}%` }}
            />
          </div>
        </div>
      ))}
      {overflow > 0 && <p className="token-10 text-glass-muted pt-0.5">+{overflow} more</p>}
    </div>
  );
};

const DismissBadge: React.FC<{ type: DismissedConversation["dismiss_type"] }> = ({ type }) => {
  const isDead = type === "dead";
  return (
    <span
      className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
        isDead ? "text-ds-red bg-ds-red/10" : "text-ds-amber bg-ds-amber/10"
      }`}
    >
      {isDead ? "Killed" : "Dismissed"}
    </span>
  );
};

export const DismissedLeadsPanel: React.FC<DismissedLeadsPanelProps> = ({
  organizationId,
  setterIds,
  dateRange,
  timezone,
  setterNames,
  onOpenConversation,
  total,
  byDismisser,
  byAssignee,
  leaderboardError,
  leaderboardLoading,
  onViewGrid,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [leaderboardTab, setLeaderboardTab] = useState<"dismisser" | "assignee">("dismisser");
  const { dismissed, count, isLoading, error, refresh } = useDismissedConversations({
    organizationId,
    setterIds,
    dateRange,
    timezone,
    // orgScope (S4b): drop the setterIds IN-filter so the list body shows the
    // same org-wide pile the leaderboard counts — incl. unassigned dismissals,
    // which `.in(setterIds)` can never match (NULL ghl_assigned_user_id).
    orgScope: true,
  });

  return (
    <div className="liquid-glass-light">
      {/* Header bar — the "marker": how many were dismissed in this period.
          Two sibling controls (expand + view-grid), NOT a nested button. */}
      <div className="w-full flex items-center justify-between gap-2 px-4 py-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          data-testid="dismissed-panel-expand"
          className="flex-1 min-w-0 flex items-center justify-between gap-2 -mx-1 px-1 py-1 rounded-lg hover:bg-black/[0.02] transition-colors"
        >
          <span className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-glass">Dismissed Leads</span>
            <span
              data-testid="dismissed-count-pill"
              className={`text-xs font-mono tabular-nums px-2 py-0.5 rounded-full ${
                leaderboardError ? "text-ds-red bg-ds-red/10" : "text-ds-amber bg-ds-amber/10"
              }`}
            >
              {leaderboardError ? "!" : leaderboardLoading ? "…" : total}
            </span>
            <span className="text-xs text-glass-muted">
              {leaderboardError ? "load failed" : "in range"}
            </span>
          </span>
          <NavIcons.ChevronDown
            className={`w-4 h-4 text-glass-muted transition-transform shrink-0 ${
              expanded ? "rotate-180" : ""
            }`}
          />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onViewGrid();
          }}
          data-testid="dismissed-view-grid"
          className="liquid-glass-light shrink-0 px-3 py-1.5 token-11 text-glass-secondary hover:text-glass transition-colors"
        >
          View grid
        </button>
      </div>

      {/* Expanded body — leaderboard strip ABOVE the list */}
      {expanded && (
        <>
          {/* Dismissals leaderboard strip — "who dismisses the most" (both cuts) */}
          <div
            data-testid="dismissed-leaderboard"
            className="px-4 pt-3 pb-3 border-t border-black/[0.06]"
          >
            <p className="token-10u text-glass-muted mb-2">Dismissals in range</p>
            {total === 0 && !leaderboardLoading && !leaderboardError ? (
              <p className="token-13 text-glass-muted">No dismissals in range</p>
            ) : (
              <>
                <div className="flex gap-1 mb-3 bg-black/[0.05] rounded-lg p-1 w-fit">
                  <button
                    type="button"
                    onClick={() => setLeaderboardTab("dismisser")}
                    className={`anim-tap px-3 py-1 token-11 rounded-md transition-colors ${
                      leaderboardTab === "dismisser"
                        ? "bg-black/[0.03] text-glass shadow-sm"
                        : "text-glass-secondary hover:text-glass"
                    }`}
                  >
                    Who dismissed
                  </button>
                  <button
                    type="button"
                    onClick={() => setLeaderboardTab("assignee")}
                    className={`anim-tap px-3 py-1 token-11 rounded-md transition-colors ${
                      leaderboardTab === "assignee"
                        ? "bg-black/[0.03] text-glass shadow-sm"
                        : "text-glass-secondary hover:text-glass"
                    }`}
                  >
                    Whose leads
                  </button>
                </div>
                {leaderboardTab === "dismisser" ? (
                  <LeaderboardBars
                    rows={byDismisser.map((d) => ({ key: d.id, label: d.name, count: d.count }))}
                  />
                ) : (
                  <LeaderboardBars
                    rows={byAssignee.map((a) => ({
                      key: a.ghlUserId ?? "unassigned",
                      label:
                        a.ghlUserId === null
                          ? "Unassigned"
                          : (setterNames?.[a.ghlUserId] ?? "(unknown)"),
                      count: a.count,
                    }))}
                  />
                )}
              </>
            )}
          </div>

          {/* Expanded list */}
          <div className="anim-drawer-in max-h-96 overflow-y-auto border-t border-black/[0.06]">
            {error ? (
              <div className="px-4 py-6 text-center text-xs text-ds-red">
                Couldn&apos;t load dismissed leads.{" "}
                <button type="button" onClick={refresh} className="underline hover:opacity-80">
                  Retry
                </button>
              </div>
            ) : count === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-glass-muted">
                {isLoading ? "Loading…" : "No leads dismissed in this range."}
              </div>
            ) : (
              <ul className="divide-y divide-black/[0.04]">
                {dismissed.map((c) => {
                  const setterName = c.ghl_assigned_user_id
                    ? setterNames?.[c.ghl_assigned_user_id]
                    : null;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() =>
                          onOpenConversation({
                            id: c.id,
                            contactName: c.contact_name || "Unknown",
                            leadInstagram: null,
                          })
                        }
                        className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-black/[0.02] transition-colors"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm text-glass truncate">
                            {c.contact_name || "Unknown"}
                          </span>
                          {setterName && (
                            <span className="block text-[11px] text-glass-muted truncate">
                              {setterName}
                            </span>
                          )}
                        </span>
                        <span className="flex items-center gap-2 shrink-0">
                          {c.suffering_dismissed_at && (
                            <span className="text-[11px] font-mono tabular-nums text-glass-muted">
                              {formatDateShort(new Date(c.suffering_dismissed_at), timezone)}
                            </span>
                          )}
                          <DismissBadge type={c.dismiss_type} />
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
};
